import cn from 'classnames';
import { useEffect, useState } from 'react';
import type { Divider, RelativeLink } from '../../pages/api/types';
import Icon from '../generic/Icon';
import { useScrollButtons } from '../generic/ScrollableDiv';
import type { KeyedArray } from '../sanity/types';
import { trackHeaderLinkClick } from '../utils/trackLinkClick';
import * as style from './HeaderCategoryBar.module.css';

export default function HeaderCategoryBar({ links }: { links: KeyedArray<RelativeLink | Divider> }) {
    const hidden = useScrollHiding();

    const { isPreviousButtonVisible, isNextButtonVisible, scrollLeft, scrollRight, listRef } = useScrollButtons([links]);

    return (
        <div
            className={cn(style.headerCategoryBar, { [style.scrollHidden]: hidden })}
            id="header-categories"
            tabIndex={-1}
            onClick={trackHeaderLinkClick}
        >
            <nav className="section__inner-container">
                {isPreviousButtonVisible && (
                    <button className={style.scrollLeft} onClick={scrollLeft} tabIndex={-1} aria-hidden="true">
                        <Icon className="icon-chevron-left" />
                    </button>
                )}
                <menu ref={listRef}>
                    {links
                        .filter((t) => t._type === 'namedRelativeLink')
                        .map(({ _key, url, name }) => (
                            <li key={_key}>
                                <a href={url} rel="nofollow">
                                    {name}
                                </a>
                            </li>
                        ))}
                </menu>
                {isNextButtonVisible && (
                    <button className={style.scrollRight} onClick={scrollRight} tabIndex={-1} aria-hidden="true">
                        <span>Se flere</span> <Icon className="icon-chevron-right" />
                    </button>
                )}
            </nav>
        </div>
    );
}

function useScrollHiding() {
    const [hidden, setHidden] = useState(false);

    useEffect(() => {
        let position = window.scrollY;

        const handler = () => {
            const scroll = window.scrollY;

            if (scroll < position) {
                setHidden(false);
            } else if (scroll >= 200) {
                setHidden(true);
            }
            position = scroll;
        };

        document.addEventListener('scroll', handler, { passive: true });

        return () => document.removeEventListener('scroll', handler);
    }, []);

    return hidden;
}
